import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { ConfigContext } from '../../../../context';
import {
  OrganisationTagType,
  OrganisationUsersFilters,
  OrganisationUsersFiltersSection,
  useOrganisationUsersFiltersQuery,
  UserGender,
  UserInvitedBy,
  UserRoles,
  UserStatus,
} from '../../../../services';
import {
  cookieOptions,
  Filter,
  FilterGroup,
  FilterValue,
  optArrItem,
  ORG_DASH_USERS,
  useAppSelector,
  useFilter,
  useTagsFilter,
} from '../../../../shared';
import { GetOrgUsersArgs } from '../Users';

import classes from './UsersFilters.module.scss';

interface UsersFiltersProps {
  totalItems: number;
  getUsers: (args: GetOrgUsersArgs) => void;
  channelId?: number;
}

export const UsersFilters: FunctionComponent<UsersFiltersProps> = memo(
  ({ getUsers, totalItems, channelId }) => {
    const { t } = useTranslation();

    const { getGroupValue } = useFilter();

    const { channels } = useAppSelector(({ channels }) => channels);

    const { fields: profileFields } = useContext(ConfigContext).config.elements.profile;

    const { data: organisationUsersFilters = [], isFetching: isFetchingOrganisationUsersFilters } =
      useOrganisationUsersFiltersQuery({
        filters: {
          sections: [
            OrganisationUsersFiltersSection.DEPARTMENT,
            OrganisationUsersFiltersSection.POSITION,
            OrganisationUsersFiltersSection.SUBSCRIPTION,
          ],
        },
      });

    const [cookies, setCookie] = useCookies([ORG_DASH_USERS]);

    const orgDashUsersCookies = useMemo(() => {
      return { ...cookies[ORG_DASH_USERS] };
    }, [cookies]);

    const [filter, setFilter] = useState<FilterValue[]>(orgDashUsersCookies.filter ?? []);

    const { group: userTagsGroup } = useTagsFilter({ filter, types: [OrganisationTagType.USER] });

    const channelDropdownOptions = useMemo(
      () =>
        channels.map(({ id, name }) => {
          return { id, title: name };
        }),
      [channels]
    );

    const channelsGroup = useMemo(() => {
      return {
        id: 'channels',
        title: t('common.channels_other'),
        value:
          getGroupValue({
            filter,
            groupId: 'channels',
            multiCheck: true,
            propToMap: 'id',
          }) ?? [],
        dropdown: {
          options: channelDropdownOptions,
          staticListPosition: false,
          multiCheck: true,
        },
      };
    }, [channelDropdownOptions, filter, getGroupValue, t]);

    const gendersGroup = useMemo(() => {
      const title = t(`${profileFields['gender'].localiseKey ?? 'common.gender'}`);

      return {
        id: 'genders',
        title,
        items: Object.keys(UserGender).map((gender) => {
          return {
            id: gender,
            label: t(`genderTypes.${gender}`),
            value: gender !== UserGender.NOT_SELECTED ? gender : null,
          };
        }),
        multiCheck: true,
        allowReset: true,
        allowCollapse: true,
      };
    }, [profileFields, t]);

    const orgGroups = useMemo(() => {
      if (isFetchingOrganisationUsersFilters) {
        return [];
      }

      return organisationUsersFilters
        .filter(({ values }) => values.length)
        .map(({ name, values }) => {
          const filter = name.toLowerCase();

          const title = t(
            `${
              profileFields[filter as keyof typeof profileFields].localiseKey ?? `common.${filter}`
            }`
          );

          return {
            id: name,
            title,
            items: values.map(({ value }) => {
              return { id: value, label: value, value };
            }),
            multiCheck: true,
            allowReset: true,
            allowCollapse: true,
          };
        });
    }, [isFetchingOrganisationUsersFilters, organisationUsersFilters, profileFields, t]);

    const rolesGroup = useMemo(() => {
      const title = t(`${profileFields['role'].localiseKey ?? 'common.roles'}`);

      return {
        id: 'roles',
        title,
        items: Object.values(UserRoles)
          .filter((role) => typeof role !== 'number')
          .map((role) => {
            return {
              id: role,
              label: t(`roles.${role}`),
              value: UserRoles[role as keyof typeof UserRoles],
            };
          }),
        multiCheck: true,
        allowReset: true,
        allowCollapse: true,
      };
    }, [profileFields, t]);

    const onboardingGroup = useMemo(() => {
      return {
        id: 'onboarding',
        title: t('common.onboarding'),
        items: Object.keys(UserInvitedBy).map((onboardingMethod) => {
          return {
            id: onboardingMethod,
            label: t(`onboardingMethods.${onboardingMethod}`),
            value: onboardingMethod !== UserInvitedBy.UNKNOWN ? onboardingMethod : null,
          };
        }),
        multiCheck: true,
        allowReset: true,
        allowCollapse: true,
      };
    }, [t]);

    const statusesGroup = useMemo(() => {
      return {
        id: 'statuses',
        title: t('common.status'),
        items: Object.keys(UserStatus).map((status) => {
          return {
            id: status,
            label: t(`common.${status.toLowerCase()}`),
            value: status,
          };
        }),
        multiCheck: true,
        allowReset: true,
        allowCollapse: true,
      };
    }, [t]);

    const locationGroup = useMemo(() => {
      const title = t(`${profileFields['location'].localiseKey ?? 'common.location'}`);

      return {
        id: 'location',
        title,
        search: { placeholder: title },
        value: getGroupValue({ filter, groupId: 'location' }) ?? '',
        allowReset: true,
      };
    }, [filter, getGroupValue, profileFields, t]);

    const groups = useMemo(() => {
      return [
        ...optArrItem(Boolean(!channelId), channelsGroup),
        gendersGroup,
        ...orgGroups,
        rolesGroup,
        onboardingGroup,
        statusesGroup,
        userTagsGroup,
        locationGroup,
      ] as FilterGroup[];
    }, [
      channelId,
      channelsGroup,
      gendersGroup,
      locationGroup,
      onboardingGroup,
      orgGroups,
      rolesGroup,
      statusesGroup,
      userTagsGroup,
    ]);

    const onChange = useCallback(
      (filter: FilterValue[]) => {
        const filterPositions = getGroupValue({
          filter,
          groupId: OrganisationUsersFiltersSection.POSITION,
          multiCheck: true,
        });

        const filterDepartments = getGroupValue({
          filter,
          groupId: OrganisationUsersFiltersSection.DEPARTMENT,
          multiCheck: true,
        });

        const filterSubscriptions = getGroupValue({
          filter,
          groupId: OrganisationUsersFiltersSection.SUBSCRIPTION,
          multiCheck: true,
        });

        const genders = getGroupValue({ filter, groupId: 'genders', multiCheck: true });

        const organisationTagsId = getGroupValue({
          filter,
          groupId: userTagsGroup.id,
          propToMap: 'id',
          multiCheck: true,
        });

        const rolesId = getGroupValue({ filter, groupId: 'roles', multiCheck: true });

        const queryLocation = getGroupValue({ filter, groupId: 'location' });

        const channelsId = getGroupValue({
          filter,
          groupId: 'channels',
          multiCheck: true,
          propToMap: 'id',
        });

        const onboardingMethods = getGroupValue({
          filter,
          groupId: 'onboarding',
          multiCheck: true,
        });

        const statuses = getGroupValue({ filter, groupId: 'statuses', multiCheck: true });

        const requestFilters: OrganisationUsersFilters = {
          ...(filterPositions.length && { filterPositions }),
          ...(filterDepartments.length && { filterDepartments }),
          ...(filterSubscriptions.length && { filterSubscriptions }),
          ...(genders.length && { genders }),
          ...(organisationTagsId.length && { organisationTagsId }),
          ...(rolesId.length && { rolesId }),
          ...(onboardingMethods.length && { onboardingMethods }),
          ...(statuses.length && { statuses }),
          ...(!channelId && channelsId.length && { channelsId }),
          ...(queryLocation && { queryLocation }),
        };

        setCookie(
          ORG_DASH_USERS,
          { ...orgDashUsersCookies, requestFilters, filter },
          cookieOptions()
        );

        getUsers({ page: 1, filters: requestFilters });

        setFilter(filter);
      },
      [channelId, getGroupValue, getUsers, orgDashUsersCookies, setCookie, userTagsGroup.id]
    );

    if (isFetchingOrganisationUsersFilters) {
      return (
        <Skeleton
          className={classes['filters__loader']}
          width={'18.75rem'}
          height={'2rem'}
          count={5}
        />
      );
    }

    return (
      <div className={classes['filters']}>
        <div className={classes['filters__total-items']}>
          {t('orgDashboardUsers.total-items', { totalItems })}
        </div>
        <Filter groups={groups} filter={filter} onChange={onChange} />
      </div>
    );
  }
);
